import { useState } from 'react';
import { Link } from 'react-router-dom';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

function ContactForm() {
  const [selectedModel, setSelectedModel] = useState<'vendor' | 'partnership' | ''>('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));
    setStatus('submitting');
    try {
      const res = await fetch('https://readdy.ai/api/form/d79gtijll6jbq2vddnk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setSelectedModel('');
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center py-12">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${selectedModel === 'partnership' ? 'bg-[#059669]/10' : 'bg-[#1B4B7F]/10'}`}>
          <i className={`ri-checkbox-circle-line text-5xl ${selectedModel === 'partnership' ? 'text-[#059669]' : 'text-[#1B4B7F]'}`}></i>
        </div>
        <h3 className="text-2xl font-black text-gray-900 mb-3">Message Received!</h3>
        <p className="text-gray-600 text-sm leading-relaxed max-w-xs mb-6">
          Our team will be in touch within 24 hours to discuss your {selectedModel === 'partnership' ? 'Partnership' : 'MSO Vendor'} inquiry.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="text-sm font-semibold text-[#1B4B7F] hover:underline cursor-pointer"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
      {/* Model Selection — top of form */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">I&apos;m interested in...</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setSelectedModel('vendor')}
            className={`relative rounded-xl border-2 p-4 text-left transition-all cursor-pointer ${
              selectedModel === 'vendor'
                ? 'border-[#1B4B7F] bg-[#F0F7FF]'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            {selectedModel === 'vendor' && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-[#1B4B7F] rounded-full flex items-center justify-center">
                <i className="ri-check-line text-white text-xs"></i>
              </div>
            )}
            <div className="w-8 h-8 bg-[#1B4B7F]/10 rounded-lg flex items-center justify-center mb-2">
              <i className="ri-briefcase-line text-[#1B4B7F] text-base"></i>
            </div>
            <div className="text-sm font-bold text-gray-900">MSO Vendor</div>
            <div className="text-xs text-gray-500 mt-0.5">Hire us as your vendor</div>
          </button>
          <button
            type="button"
            onClick={() => setSelectedModel('partnership')}
            className={`relative rounded-xl border-2 p-4 text-left transition-all cursor-pointer ${
              selectedModel === 'partnership'
                ? 'border-[#059669] bg-[#F0FDF4]'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            {selectedModel === 'partnership' && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-[#059669] rounded-full flex items-center justify-center">
                <i className="ri-check-line text-white text-xs"></i>
              </div>
            )}
            <div className="w-8 h-8 bg-[#059669]/10 rounded-lg flex items-center justify-center mb-2">
              <i className="ri-vip-crown-line text-[#059669] text-base"></i>
            </div>
            <div className="text-sm font-bold text-gray-900">Partnership</div>
            <div className="text-xs text-gray-500 mt-0.5">$500K + 40% equity</div>
          </button>
        </div>
        <input type="hidden" name="interested_in" value={selectedModel === 'vendor' ? 'MSO Vendor Model' : selectedModel === 'partnership' ? 'Partnership Model ($500K + 40% Equity)' : ''} />
      </div>

      {selectedModel === 'partnership' && (
        <div className="bg-[#F0FDF4] border border-[#059669]/30 rounded-xl px-4 py-3 flex items-start gap-3">
          <i className="ri-information-line text-[#059669] text-lg mt-0.5 flex-shrink-0"></i>
          <p className="text-xs text-gray-600 leading-relaxed">
            <strong className="text-[#059669]">Partnership inquiries</strong> are handled by our senior team. Expect a confidential call within 24 hours.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">First Name</label>
          <input
            type="text"
            name="first_name"
            required
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#1B4B7F] focus:ring-2 focus:ring-[#1B4B7F]/20 outline-none transition-all text-sm"
            placeholder="John"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">Last Name</label>
          <input
            type="text"
            name="last_name"
            required
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#1B4B7F] focus:ring-2 focus:ring-[#1B4B7F]/20 outline-none transition-all text-sm"
            placeholder="Doe"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">Email</label>
        <input
          type="email"
          name="email"
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#1B4B7F] focus:ring-2 focus:ring-[#1B4B7F]/20 outline-none transition-all text-sm"
          placeholder="john@example.com"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">Phone</label>
        <input
          type="tel"
          name="phone"
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#1B4B7F] focus:ring-2 focus:ring-[#1B4B7F]/20 outline-none transition-all text-sm"
          placeholder="(939) 399-2059"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">Practice Type</label>
        <select
          name="practice_type"
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#1B4B7F] focus:ring-2 focus:ring-[#1B4B7F]/20 outline-none transition-all text-sm cursor-pointer"
        >
          <option value="">Select your practice type</option>
          <option>Private Practice (Single Provider)</option>
          <option>Group Clinic / Multi-Provider</option>
          <option>Specialty Center</option>
          <option>Pharmacy</option>
          <option>Laboratory</option>
          <option>Diagnostic Center</option>
          <option>Other</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Message
          <span className="text-gray-400 font-normal ml-2 text-xs">({charCount}/500)</span>
        </label>
        <textarea
          name="message"
          rows={4}
          maxLength={500}
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#1B4B7F] focus:ring-2 focus:ring-[#1B4B7F]/20 outline-none transition-all resize-none text-sm"
          placeholder="Tell us about your practice and what you're looking to achieve..."
        ></textarea>
      </div>

      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
          Something went wrong. Please try again or call us at (939) 399-2059.
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting' || !selectedModel}
        className={`w-full px-8 py-4 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
          selectedModel === 'partnership'
            ? 'bg-[#059669] text-white hover:bg-[#047857] disabled:opacity-50'
            : 'bg-[#1B4B7F] text-white hover:bg-[#153a63] disabled:opacity-50'
        }`}
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            Sending...
          </>
        ) : selectedModel === 'partnership' ? (
          <>
            <i className="ri-vip-crown-line"></i>
            Request Partnership Conversation
          </>
        ) : (
          'Send Message'
        )}
      </button>

      {!selectedModel && (
        <p className="text-center text-xs text-gray-400">Please select a model above to continue</p>
      )}
    </form>
  );
}

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setScrolled(window.scrollY > 50);
    });
  }

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Newsletter submission logic
    alert('Thank you for subscribing!');
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-gradient-to-b from-[#0A1628] to-[#1B4B7F]'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/50f4002bde5ecf02750e3107d0496ef3/6f24c25eed393f63407e0e21645fee7d.png" 
                alt="MedxMSO Logo" 
                className="h-12 w-auto"
              />
              <span className={`text-xl font-black tracking-tight whitespace-nowrap ${scrolled ? 'text-[#1B4B7F]' : 'text-white'}`}>MedxMSO</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#1B4B7F] font-semibold' : 'text-white font-semibold'}`}>Home</Link>
              <Link to="/services" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Services</Link>
              <Link to="/pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Models</Link>
              <Link to="/mso-pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Vendor Pricing</Link>
              <Link to="/partnership" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#059669] hover:text-[#047857]' : 'text-[#6EE7B7] hover:text-white'}`}>Partnership</Link>
              <Link to="/why-mso" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Why MSO?</Link>
              <Link to="/audit" className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#BE185D] hover:text-[#9D174D]' : 'text-[#F9A8D4] hover:text-white'}`}>Request Audit</Link>
              <a href="#contact" className={`px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'bg-[#1B4B7F] text-white hover:bg-[#153a63]' : 'bg-white/20 border border-white/40 text-white hover:bg-white/30'}`}>
                Get Started
              </a>
            </div>

            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${scrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-6 py-4 space-y-3">
              <Link to="/" className="block text-sm font-semibold text-[#1B4B7F] cursor-pointer">Home</Link>
              <Link to="/services" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Services</Link>
              <Link to="/pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Models</Link>
              <Link to="/mso-pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Vendor Pricing</Link>
              <Link to="/partnership" className="block text-sm font-semibold text-[#059669] cursor-pointer">Partnership</Link>
              <Link to="/why-mso" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Why MSO?</Link>
              <Link to="/audit" className="block text-sm font-semibold text-[#BE185D] cursor-pointer">Request Audit</Link>
              <a href="#contact" className="block bg-[#1B4B7F] text-white px-6 py-2.5 rounded-full text-sm font-medium text-center hover:bg-[#153a63] transition-colors cursor-pointer">
                Get Started
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B4B7F] via-[#2563a8] to-white"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <p className="text-xs uppercase tracking-[2px] text-blue-200 mb-6 font-semibold">HEALTHCARE MANAGEMENT EXCELLENCE</p>
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6">
                Empowering Healthcare<br />
                Providers to Focus on<br />
                What Matters <span className="italic">Most</span>
              </h1>
              <p className="text-lg text-blue-100 mb-8 leading-relaxed max-w-xl">
                Comprehensive administrative and operational support for medical practices, clinics, pharmacies, laboratories, and diagnostic centers across the healthcare spectrum
              </p>
              
              {/* CTAs */}
              <div className="flex flex-wrap gap-4 mb-8">
                <a href="#contact" className="bg-[#1B4B7F] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#153a63] transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer shadow-lg">
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </a>
                <Link to="/partnership" className="bg-[#059669] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#047857] transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer shadow-lg">
                  <i className="ri-vip-crown-line"></i>
                  Explore Partnership
                </Link>
                <a href="#services" className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-white hover:text-[#1B4B7F] transition-all whitespace-nowrap cursor-pointer">
                  Explore Services
                </a>
              </div>

              {/* Credentials */}
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/10 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-2">
                  <i className="ri-shield-check-line text-green-300"></i>
                  HIPAA Compliant
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-2">
                  <i className="ri-hospital-line text-blue-300"></i>
                  500+ Practices Supported
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/ad725ea4d0d7e891c51d2c8da436b986.jpg" 
                  alt="Healthcare Team Collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[2px] text-[#0D9488] mb-4 font-semibold">COMPREHENSIVE MSO SOLUTIONS</p>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              End-to-End Support for<br />
              <span className="font-extrabold">Healthcare Operations</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white rounded-2xl p-12 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-stethoscope-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Practice Management</h3>
              <p className="text-gray-600 leading-relaxed">
                Streamline daily operations with comprehensive practice management solutions including scheduling, patient flow optimization, and operational efficiency consulting.
              </p>
            </div>

            {/* Service Card 2 */}
            <div className="bg-white rounded-2xl p-12 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Revenue Cycle Management</h3>
              <p className="text-gray-600 leading-relaxed">
                Maximize revenue with expert billing, coding, claims management, and accounts receivable optimization to ensure healthy cash flow for your practice.
              </p>
            </div>

            {/* Service Card 3 */}
            <div className="bg-white rounded-2xl p-12 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Compliance & Credentialing</h3>
              <p className="text-gray-600 leading-relaxed">
                Stay compliant with ever-changing healthcare regulations. We handle credentialing, HIPAA compliance, and regulatory documentation management.
              </p>
            </div>

            {/* Service Card 4 */}
            <div className="bg-white rounded-2xl p-12 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-team-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">HR & Staffing Solutions</h3>
              <p className="text-gray-600 leading-relaxed">
                Complete human resources support including recruitment, onboarding, payroll management, benefits administration, and staff training programs.
              </p>
            </div>

            {/* Service Card 5 */}
            <div className="bg-white rounded-2xl p-12 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-global-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Technology Integration</h3>
              <p className="text-gray-600 leading-relaxed">
                Implement and optimize healthcare technology systems including EHR/EMR, practice management software, and telehealth platforms for seamless operations.
              </p>
            </div>

            {/* Service Card 6 */}
            <div className="bg-white rounded-2xl p-12 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-calculator-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Financial Operations</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive financial management including bookkeeping, financial reporting, budgeting, and strategic financial planning for sustainable growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left Images */}
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-3xl overflow-hidden border border-gray-200">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20medical%20facility%20exterior%20with%20contemporary%20healthcare%20building%20architecture%20featuring%20glass%20windows%20clean%20lines%20professional%20medical%20center%20entrance%20with%20landscaping%20and%20welcoming%20atmosphere%20representing%20trusted%20healthcare%20management%20services%20organization&width=700&height=400&seq=about-facility&orientation=landscape" 
                  alt="Medical Facility"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-3xl overflow-hidden border border-gray-200">
                <img 
                  src="https://readdy.ai/api/search-image?query=Healthcare%20management%20team%20meeting%20with%20diverse%20professionals%20collaborating%20around%20conference%20table%20discussing%20medical%20practice%20operations%20with%20laptops%20documents%20and%20charts%20in%20modern%20bright%20office%20setting%20representing%20MSO%20expertise%20and%20teamwork&width=700&height=300&seq=about-team-meeting&orientation=landscape" 
                  alt="Team Meeting"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>

            {/* Right Content */}
            <div className="lg:col-span-3">
              <p className="text-xs uppercase tracking-[2px] text-[#1B4B7F] mb-4 font-semibold">ABOUT MEDXMSO</p>
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-10">
                Dedicated to Healthcare<br />
                Provider Success Since 2015
              </h2>
              
              <div className="space-y-6 text-gray-600 leading-relaxed text-lg mb-12">
                <p>
                  MedxMSO is a leading Management Services Organization dedicated to empowering healthcare providers across the United States. We understand that managing a medical practice, clinic, pharmacy, laboratory, or diagnostic center requires expertise far beyond patient care. That's where we come in.
                </p>
                <p>
                  Our comprehensive suite of administrative and operational services allows healthcare professionals to focus on what they do best—providing exceptional patient care. With over eight years of experience and a team of healthcare management experts, we've helped hundreds of practices optimize their operations, increase revenue, and achieve sustainable growth.
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-8">
                <div>
                  <div className="text-5xl font-bold text-[#1B4B7F] mb-2">500+</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Practices</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-[#1B4B7F] mb-2">98%</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Retention</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-[#1B4B7F] mb-2">$50M+</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Managed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section - Entity Types */}
      <section id="clients" className="py-24 bg-gradient-to-b from-[#F0F7FF] to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-16 gap-8">
            <h2 className="text-5xl lg:text-7xl font-serif font-bold text-gray-900 leading-tight">
              Healthcare Entities<br />
              We Support
            </h2>
            <p className="text-base text-gray-600 leading-relaxed max-w-md lg:mt-4">
              From small private practices to large diagnostic centers, we provide tailored management solutions for every type of healthcare entity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-7">
            {/* Card 1 - Dark */}
            <div className="bg-[#0F172A] rounded-3xl overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-300">
              <div className="p-10">
                <h3 className="text-3xl font-bold text-white mb-4">Medical Practices</h3>
                <p className="text-gray-300 text-base mb-8">
                  Private practices, group clinics, specialty centers, and ambulatory care facilities
                </p>
              </div>
              <div className="relative h-80">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20medical%20clinic%20interior%20with%20clean%20professional%20examination%20room%20featuring%20medical%20equipment%20comfortable%20patient%20area%20contemporary%20healthcare%20design%20with%20examination%20table%20medical%20instruments%20and%20bright%20welcoming%20atmosphere%20representing%20private%20practice%20medical%20facility&width=500&height=600&seq=client-medical-practice&orientation=portrait" 
                  alt="Medical Practice"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-10">
                <button className="border-2 border-white text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-white hover:text-[#0F172A] transition-all whitespace-nowrap">
                  Learn More
                </button>
              </div>
            </div>

            {/* Card 2 - Light */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg group cursor-pointer hover:shadow-2xl transition-all duration-300">
              <div className="p-10">
                <div className="inline-block bg-[#0D9488] text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-6">
                  POPULAR
                </div>
                <div className="relative h-64 mb-8 rounded-2xl overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Modern%20pharmacy%20and%20laboratory%20interior%20with%20organized%20medication%20shelves%20pharmaceutical%20equipment%20lab%20testing%20instruments%20clean%20professional%20healthcare%20environment%20with%20prescription%20counter%20medical%20supplies%20and%20laboratory%20workstations%20representing%20pharmacy%20and%20lab%20services&width=500&height=400&seq=client-pharmacy-lab&orientation=squarish" 
                    alt="Pharmacies & Labs"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Pharmacies & Labs</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-8">
                  Retail pharmacies, compounding pharmacies, clinical laboratories, and testing facilities
                </p>
                <button className="bg-[#1B4B7F] text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-[#153a63] transition-all whitespace-nowrap">
                  Explore Solutions
                </button>
              </div>
            </div>

            {/* Card 3 - Light */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg group cursor-pointer hover:shadow-2xl transition-all duration-300">
              <div className="p-10">
                <div className="inline-block bg-[#F59E0B] text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-6">
                  SPECIALIZED
                </div>
                <div className="relative h-64 mb-8 rounded-2xl overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Advanced%20diagnostic%20center%20interior%20with%20modern%20medical%20imaging%20equipment%20MRI%20CT%20scan%20machines%20radiology%20workstations%20high-tech%20healthcare%20facility%20with%20sophisticated%20diagnostic%20technology%20professional%20medical%20environment%20representing%20diagnostic%20imaging%20center&width=500&height=400&seq=client-diagnostic-center&orientation=squarish" 
                    alt="Diagnostic Centers"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Diagnostic Centers</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-8">
                  Imaging centers, radiology facilities, pathology labs, and specialized diagnostic services
                </p>
                <button className="bg-[#1B4B7F] text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-[#153a63] transition-all whitespace-nowrap">
                  Explore Solutions
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#FBF9F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 bg-[#1B4B7F] rounded-full"></div>
              <p className="text-xs uppercase tracking-[2px] text-gray-600 font-semibold">CLIENT SUCCESS STORIES</p>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4">
              Trusted by Healthcare<br />
              Providers Nationwide
            </h2>
            <p className="text-base text-gray-600">See how MedxMSO transforms healthcare operations</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-3xl p-9 hover:shadow-xl transition-all duration-300">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#F59E0B] text-lg"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Game-changer for our practice</h4>
              <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                MedxMSO transformed our billing process and increased our revenue by 35% in just six months. Their team is professional, responsive, and truly understands healthcare operations.
              </p>
              <div className="flex items-center gap-3">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20female%20doctor%20portrait%20with%20confident%20smile%20wearing%20white%20medical%20coat%20stethoscope%20in%20modern%20healthcare%20setting%20representing%20medical%20practice%20administrator&width=100&height=100&seq=testimonial-avatar-1&orientation=squarish" 
                  alt="Dr. Sarah Mitchell"
                  className="w-12 h-12 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <div className="font-bold text-gray-900 text-base">Dr. Sarah Mitchell</div>
                  <div className="text-sm text-gray-600">Practice Administrator, Family Medicine Clinic</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-3xl p-9 hover:shadow-xl transition-all duration-300">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#F59E0B] text-lg"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Exceptional compliance support</h4>
              <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                Navigating healthcare regulations was overwhelming until we partnered with MedxMSO. They handle all our credentialing and compliance needs flawlessly.
              </p>
              <div className="flex items-center gap-3">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20male%20healthcare%20administrator%20portrait%20with%20friendly%20expression%20wearing%20business%20attire%20in%20modern%20medical%20office%20representing%20clinic%20operations%20manager&width=100&height=100&seq=testimonial-avatar-2&orientation=squarish" 
                  alt="Michael Chen"
                  className="w-12 h-12 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <div className="font-bold text-gray-900 text-base">Michael Chen</div>
                  <div className="text-sm text-gray-600">Operations Manager, Orthopedic Clinic</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-3xl p-9 hover:shadow-xl transition-all duration-300">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#F59E0B] text-lg"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Best decision we ever made</h4>
              <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                Our pharmacy operations have never been smoother. MedxMSO's technology integration and financial management services are top-notch.
              </p>
              <div className="flex items-center gap-3">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20female%20pharmacist%20portrait%20with%20warm%20smile%20wearing%20white%20pharmacy%20coat%20in%20modern%20pharmacy%20setting%20representing%20pharmacy%20operations%20director&width=100&height=100&seq=testimonial-avatar-3&orientation=squarish" 
                  alt="Jennifer Rodriguez"
                  className="w-12 h-12 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <div className="font-bold text-gray-900 text-base">Jennifer Rodriguez</div>
                  <div className="text-sm text-gray-600">Director, Community Pharmacy</div>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-white rounded-3xl p-9 hover:shadow-xl transition-all duration-300">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#F59E0B] text-lg"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Outstanding HR support</h4>
              <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                Recruiting and retaining quality staff was our biggest challenge. MedxMSO's HR solutions have been invaluable to our growth.
              </p>
              <div className="flex items-center gap-3">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20male%20doctor%20portrait%20with%20confident%20demeanor%20wearing%20medical%20scrubs%20in%20modern%20diagnostic%20center%20representing%20medical%20director&width=100&height=100&seq=testimonial-avatar-4&orientation=squarish" 
                  alt="Dr. James Patterson"
                  className="w-12 h-12 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <div className="font-bold text-gray-900 text-base">Dr. James Patterson</div>
                  <div className="text-sm text-gray-600">Medical Director, Diagnostic Imaging Center</div>
                </div>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-white rounded-3xl p-9 hover:shadow-xl transition-all duration-300">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#F59E0B] text-lg"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Incredible revenue improvement</h4>
              <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                The revenue cycle management team at MedxMSO reduced our claim denials by 60% and significantly improved our cash flow. Highly recommend!
              </p>
              <div className="flex items-center gap-3">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20female%20healthcare%20executive%20portrait%20with%20professional%20appearance%20wearing%20business%20suit%20in%20modern%20medical%20office%20representing%20laboratory%20administrator&width=100&height=100&seq=testimonial-avatar-5&orientation=squarish" 
                  alt="Lisa Thompson"
                  className="w-12 h-12 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <div className="font-bold text-gray-900 text-base">Lisa Thompson</div>
                  <div className="text-sm text-gray-600">Administrator, Clinical Laboratory</div>
                </div>
              </div>
            </div>

            {/* Testimonial 6 */}
            <div className="bg-white rounded-3xl p-9 hover:shadow-xl transition-all duration-300 opacity-70">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#F59E0B] text-lg"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">True healthcare partners</h4>
              <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                MedxMSO doesn't just provide services—they're true partners in our success. Their expertise has been instrumental in our practice expansion.
              </p>
              <div className="flex items-center gap-3">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20male%20healthcare%20administrator%20portrait%20with%20friendly%20smile%20wearing%20business%20casual%20attire%20in%20modern%20medical%20facility%20representing%20practice%20owner&width=100&height=100&seq=testimonial-avatar-6&orientation=squarish" 
                  alt="Dr. Robert Williams"
                  className="w-12 h-12 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <div className="font-bold text-gray-900 text-base">Dr. Robert Williams</div>
                  <div className="text-sm text-gray-600">Owner, Multi-Specialty Group Practice</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Paths Section */}
      <section className="py-24 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[3px] text-[#6EE7B7] mb-4 font-semibold">TWO WAYS TO WORK WITH US</p>
            <h2 className="text-5xl lg:text-6xl font-black text-white leading-tight mb-4">
              Choose Your Path
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              MedxMSO offers two distinct models — a vendor relationship or a true equity partnership. Both protect your clinical independence completely.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Vendor Track */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-10 hover:bg-white/10 transition-all duration-300">
              <div className="inline-flex items-center gap-2 bg-[#1B4B7F]/40 text-[#93C5FD] px-4 py-1.5 rounded-full text-xs font-bold tracking-widest mb-6">
                <i className="ri-briefcase-line"></i>
                VENDOR MODEL
              </div>
              <h3 className="text-3xl font-black text-white mb-4">MSO as Your Vendor</h3>
              <p className="text-gray-400 leading-relaxed mb-8">
                Hire MedxMSO to handle all your administrative headaches — billing, HR, compliance, IT, and operations. You stay fully independent, keep all your practice economics, and pay only a small performance-based fee.
              </p>
              <div className="space-y-3 mb-8">
                {['2.5% ops fee on net collections', 'Optional +2.5% for full RCM', '$250/provider/month base', '$2,500 one-time onboarding'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-gray-300">
                    <i className="ri-check-line text-[#93C5FD]"></i>
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Link to="/services" className="flex-1 bg-[#1B4B7F] text-white text-center px-6 py-3.5 rounded-full text-sm font-bold hover:bg-[#153a63] transition-all whitespace-nowrap cursor-pointer">
                  Explore Services
                </Link>
                <Link to="/mso-pricing" className="flex-1 border border-white/20 text-white text-center px-6 py-3.5 rounded-full text-sm font-bold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
                  View Pricing
                </Link>
              </div>
            </div>

            {/* Partnership Track */}
            <div className="bg-[#059669]/10 border border-[#059669]/30 rounded-3xl p-10 hover:bg-[#059669]/20 transition-all duration-300 relative">
              <div className="absolute top-6 right-6 bg-[#059669] text-white px-3 py-1 rounded-full text-xs font-bold">
                EXCLUSIVE
              </div>
              <div className="inline-flex items-center gap-2 bg-[#059669]/30 text-[#6EE7B7] px-4 py-1.5 rounded-full text-xs font-bold tracking-widest mb-6">
                <i className="ri-vip-crown-line"></i>
                PARTNERSHIP MODEL
              </div>
              <h3 className="text-3xl font-black text-white mb-4">Become a Co-Owner</h3>
              <p className="text-gray-400 leading-relaxed mb-8">
                For select single-practice physicians: keep your practice, earn a guaranteed $500K clinical package, and gain 40% equity in MedxMSO. Zero ops fees, zero RCM fees — just pure clinical focus and long-term wealth building.
              </p>
              <div className="space-y-3 mb-8">
                {['$500K guaranteed annual clinical pay', '40% equity ownership in MedxMSO', '0% ops or RCM fees', '100% practice ownership retained'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-gray-300">
                    <i className="ri-check-line text-[#6EE7B7]"></i>
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Link to="/partnership" className="flex-1 bg-[#059669] text-white text-center px-6 py-3.5 rounded-full text-sm font-bold hover:bg-[#047857] transition-all whitespace-nowrap cursor-pointer">
                  Learn More
                </Link>
                <Link to="/#contact" className="flex-1 border border-[#059669]/50 text-[#6EE7B7] text-center px-6 py-3.5 rounded-full text-sm font-bold hover:bg-[#059669]/20 transition-all whitespace-nowrap cursor-pointer">
                  Schedule a Call
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[2px] text-[#0D9488] mb-4 font-semibold">TRANSPARENT PRICING</p>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Performance-Based<br />
              Partnership Pricing
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Trade fixed overhead for scalable, performance-based costs. We only succeed when you succeed—no hidden fees, just aligned incentives.
            </p>
            <Link to="/pricing" className="inline-flex items-center text-[#1B4B7F] font-semibold text-base hover:gap-3 transition-all gap-2 cursor-pointer">
              View Full Pricing &amp; ROI Calculator
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mb-12">
            {/* Onboarding */}
            <div className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:shadow-xl transition-all duration-300 text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-5 mx-auto">
                <i className="ri-rocket-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Onboarding</h3>
              <div className="text-4xl font-bold text-[#1B4B7F] mb-1">$2,500</div>
              <p className="text-sm text-gray-500 mb-4">One-time setup fee</p>
              <p className="text-xs text-gray-600 leading-relaxed">Practice integration, HIPAA audits &amp; workflow mapping</p>
            </div>

            {/* Management Fee */}
            <div className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:shadow-xl transition-all duration-300 text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-5 mx-auto">
                <i className="ri-user-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Management</h3>
              <div className="text-4xl font-bold text-[#1B4B7F] mb-1">$250</div>
              <p className="text-sm text-gray-500 mb-4">Per provider / month</p>
              <p className="text-xs text-gray-600 leading-relaxed">HR support, GPO access, compliance &amp; IT infrastructure</p>
            </div>

            {/* Ops Revenue Share */}
            <div className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:shadow-xl transition-all duration-300 text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-5 mx-auto">
                <i className="ri-line-chart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Ops Share</h3>
              <div className="text-4xl font-bold text-[#1B4B7F] mb-1">2.5%</div>
              <p className="text-sm text-gray-500 mb-4">Of net collections</p>
              <p className="text-xs text-gray-600 leading-relaxed">Performance-aligned operational management &amp; growth</p>
            </div>

            {/* RCM Add-on */}
            <div className="bg-white border-2 border-[#0D9488] rounded-3xl p-8 hover:shadow-xl transition-all duration-300 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0D9488] text-white px-4 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                OPTIONAL ADD-ON
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-2xl flex items-center justify-center mb-5 mx-auto">
                <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">RCM Add-on</h3>
              <div className="text-4xl font-bold text-[#1B4B7F] mb-1">+2.5%</div>
              <p className="text-sm text-gray-500 mb-4">Of net collections</p>
              <p className="text-xs text-gray-600 leading-relaxed">Full billing, coding, denials management &amp; payer negotiations</p>
            </div>
          </div>

          {/* Summary Banner */}
          <div className="max-w-6xl mx-auto bg-gradient-to-r from-[#1B4B7F] to-[#0D9488] rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white text-center md:text-left">
              <div className="text-sm font-semibold text-blue-200 mb-1">Total Revenue Share</div>
              <div className="text-3xl font-bold">2.5% – 5.0% <span className="text-lg font-normal text-blue-200">of net collections</span></div>
              <div className="text-sm text-blue-200 mt-1">+ $250/provider/month base &nbsp;·&nbsp; $2,500 one-time onboarding</div>
            </div>
            <Link to="/pricing" className="bg-white text-[#1B4B7F] px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer flex-shrink-0">
              Calculate Your Investment
            </Link>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section id="resources" className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[2px] text-[#0D9488] mb-4 font-semibold">KNOWLEDGE CENTER</p>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Healthcare Management<br />
              Resources
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay informed with the latest insights, guides, and best practices in healthcare management.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Resource 1 */}
            <div className="bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="relative h-56 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Healthcare%20revenue%20cycle%20management%20concept%20with%20medical%20billing%20documents%20financial%20charts%20calculator%20and%20stethoscope%20on%20desk%20representing%20medical%20practice%20financial%20optimization%20and%20billing%20efficiency&width=600&height=400&seq=resource-revenue-cycle&orientation=landscape" 
                  alt="Revenue Cycle Guide"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 bg-[#1B4B7F] text-white px-4 py-1.5 rounded-full text-xs font-semibold">
                  GUIDE
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Guide to Revenue Cycle Management</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Learn how to optimize your billing processes, reduce claim denials, and improve cash flow with proven strategies.
                </p>
                <a href="#" className="text-[#1B4B7F] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                  Read More
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* Resource 2 */}
            <div className="bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="relative h-56 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Healthcare%20compliance%20and%20HIPAA%20regulations%20concept%20with%20medical%20documents%20privacy%20policy%20papers%20security%20shield%20and%20healthcare%20professional%20reviewing%20compliance%20checklist%20representing%20regulatory%20compliance%20management&width=600&height=400&seq=resource-compliance&orientation=landscape" 
                  alt="Compliance Checklist"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 bg-[#0D9488] text-white px-4 py-1.5 rounded-full text-xs font-semibold">
                  CHECKLIST
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">2024 Healthcare Compliance Checklist</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Stay compliant with the latest healthcare regulations including HIPAA, credentialing requirements, and documentation standards.
                </p>
                <a href="#" className="text-[#1B4B7F] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                  Download PDF
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* Resource 3 */}
            <div className="bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="relative h-56 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Healthcare%20technology%20integration%20with%20modern%20EHR%20EMR%20system%20on%20computer%20screens%20medical%20software%20interface%20tablets%20and%20digital%20healthcare%20tools%20representing%20practice%20management%20technology%20implementation&width=600&height=400&seq=resource-technology&orientation=landscape" 
                  alt="Technology Integration"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 bg-[#F59E0B] text-white px-4 py-1.5 rounded-full text-xs font-semibold">
                  WEBINAR
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Implementing EHR Systems Successfully</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Watch our expert webinar on choosing and implementing the right EHR system for your practice with minimal disruption.
                </p>
                <a href="#" className="text-[#1B4B7F] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                  Watch Now
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* Resource 4 */}
            <div className="bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="relative h-56 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Healthcare%20HR%20and%20staffing%20concept%20with%20diverse%20medical%20team%20recruitment%20interview%20process%20employee%20onboarding%20documents%20and%20healthcare%20professionals%20representing%20human%20resources%20management%20in%20medical%20practices&width=600&height=400&seq=resource-hr-staffing&orientation=landscape" 
                  alt="HR Best Practices"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 bg-[#1B4B7F] text-white px-4 py-1.5 rounded-full text-xs font-semibold">
                  ARTICLE
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Best Practices for Healthcare Staffing</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Discover effective strategies for recruiting, retaining, and managing healthcare staff in today's competitive market.
                </p>
                <a href="#" className="text-[#1B4B7F] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                  Read Article
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* Resource 5 */}
            <div className="bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="relative h-56 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Medical%20practice%20financial%20planning%20with%20business%20charts%20growth%20graphs%20financial%20reports%20calculator%20and%20healthcare%20business%20strategy%20documents%20representing%20financial%20operations%20and%20budgeting%20for%20medical%20practices&width=600&height=400&seq=resource-financial-planning&orientation=landscape" 
                  alt="Financial Planning"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 bg-[#0D9488] text-white px-4 py-1.5 rounded-full text-xs font-semibold">
                  WHITEPAPER
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Financial Planning for Medical Practices</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Comprehensive whitepaper on budgeting, forecasting, and financial strategy for sustainable practice growth.
                </p>
                <a href="#" className="text-[#1B4B7F] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                  Download
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* Resource 6 */}
            <div className="bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="relative h-56 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Healthcare%20practice%20growth%20and%20expansion%20concept%20with%20medical%20facility%20development%20business%20growth%20charts%20strategic%20planning%20documents%20and%20successful%20healthcare%20business%20representing%20practice%20expansion%20strategies&width=600&height=400&seq=resource-practice-growth&orientation=landscape" 
                  alt="Practice Growth"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 bg-[#1B4B7F] text-white px-4 py-1.5 rounded-full text-xs font-semibold">
                  CASE STUDY
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">How We Helped a Practice Triple Revenue</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Real-world case study showing how strategic MSO partnership led to 300% revenue growth in 18 months.
                </p>
                <a href="#" className="text-[#1B4B7F] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all cursor-pointer">
                  View Case Study
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Professional%20healthcare%20consultation%20scene%20with%20medical%20professionals%20and%20practice%20administrators%20in%20modern%20bright%20conference%20room%20discussing%20healthcare%20management%20solutions%20with%20laptops%20documents%20and%20collaborative%20atmosphere%20representing%20MSO%20partnership%20consultation&width=1920&height=800&seq=cta-consultation-bg&orientation=landscape" 
            alt="Healthcare Consultation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end">
            <div className="mb-12 lg:mb-0">
              <h2 className="text-6xl lg:text-7xl font-light text-white leading-tight mb-2">
                ready to transform
              </h2>
              <h2 className="text-6xl lg:text-7xl font-bold text-white leading-tight">
                your practice?
              </h2>
            </div>

            <div className="text-white text-xl font-light leading-relaxed max-w-md">
              <p>Schedule a free consultation with</p>
              <p>our healthcare management experts</p>
            </div>
          </div>

          <div className="mt-16">
            <a href="#contact" className="inline-flex items-center bg-white text-[#1B4B7F] px-10 py-5 rounded-full text-base font-semibold hover:bg-gray-100 transition-all group cursor-pointer">
              <span className="mr-8">Get Started Today</span>
              <div className="w-12 h-12 bg-[#0F172A] rounded-full flex items-center justify-center group-hover:bg-[#1B4B7F] transition-colors">
                <i className="ri-arrow-right-line text-white text-xl"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Contact Info */}
            <div>
              <p className="text-xs uppercase tracking-[2px] text-[#0D9488] mb-4 font-semibold">GET IN TOUCH</p>
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-8">
                Let's Discuss Your<br />
                Practice Needs
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-12">
                Our healthcare management experts are ready to help you optimize your operations and achieve your practice goals. Reach out today for a free consultation.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F0F7FF] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-[#1B4B7F] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Phone</h4>
                    <p className="text-gray-600">(939) 399-2059</p>
                    <p className="text-sm text-gray-500 mt-1">Mon-Fri 6am-6pm EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F0F7FF] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-[#1B4B7F] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Email</h4>
                    <p className="text-gray-600">info@medxmso.com</p>
                    <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F0F7FF] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-[#1B4B7F] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Offices</h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Denver, CO</p>
                        <p className="text-gray-600">2121 S Oneida St, Suite 548</p>
                        <p className="text-gray-600">Denver, CO 80224</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">San Juan, PR</p>
                        <p className="text-gray-600">1666 Av. Ponce de Leon</p>
                        <p className="text-gray-600">San Juan, PR 00909</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Contact Form */}
            <div className="bg-[#FAF8F5] rounded-3xl p-10">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="bg-[#111827] mx-10 mb-10 rounded-[32px] overflow-hidden">
      <footer className="bg-[#0A1628] mx-3 mb-3 rounded-[28px] relative overflow-hidden">
        {/* Background Watermark */}
        <div className="absolute bottom-0 left-0 right-0 text-[#1A2942] text-[180px] font-bold opacity-50 leading-none select-none pointer-events-none">
          MEDXMSO
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-12 py-16">
          <div className="grid md:grid-cols-4 gap-12">
            {/* Column 1 - Brand */}
            <div>
              <div className="text-white text-4xl font-bold mb-3">MedxMSO</div>
              <p className="text-gray-400 text-sm mb-16">Healthcare Management Excellence</p>
              <div className="text-white text-lg leading-relaxed">
                <p>Empowering providers</p>
                <p className="text-gray-400">since 2015</p>
              </div>
            </div>

            {/* Column 2 - Services */}
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Our Services</h4>
              <ul className="space-y-4">
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Practice Management</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Revenue Cycle</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Compliance</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">HR Solutions</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Technology</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Financial Operations</a></li>
              </ul>
            </div>

            {/* Column 3 - Connect */}
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Connect With Us</h4>
              <ul className="space-y-4">
                <li>
                  <a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
                    <i className="ri-linkedin-line text-xl"></i>
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
                    <i className="ri-twitter-x-line text-xl"></i>
                    Twitter
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
                    <i className="ri-facebook-line text-xl"></i>
                    Facebook
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 4 - Newsletter */}
            <div>
              <p className="text-white text-base leading-relaxed mb-6">
                Stay updated with<br />
                healthcare insights
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex items-center gap-0">
                <input 
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 bg-transparent border border-[#374151] text-white placeholder-gray-500 px-5 py-3 rounded-l-full text-sm outline-none focus:border-[#1B4B7F] transition-colors"
                  required
                />
                <button 
                  type="submit"
                  className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer flex-shrink-0 -ml-6"
                >
                  <i className="ri-arrow-right-line text-[#0A1628] text-xl"></i>
                </button>
              </form>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2024 MedxMSO. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}
